/**
 * AddressNormalizerL2.ts
 * Normalización agresiva de direcciones (Nivel 2)
 *
 * USO EXCLUSIVO para matching interno (fuzzy search, comparación
 * documento vs referencia). NUNCA mostrar al usuario.
 *
 * Transformaciones L2 (sobre resultado L1):
 * 1. Aplica L1 completo como base
 * 2. Strip artículos y preposiciones (DE, DEL, LA, LOS, EL, AL...)
 * 3. Strip tokens numéricos (portales, km) para matching por nombre
 * 4. Reorder: tipo vía siempre primero (ya garantizado por L1)
 * 5. Strip palabras genéricas redundantes (MUNICIPAL, PUBLICO, NUEVO...)
 * 6. Deduplicación de tokens repetidos
 * 7. Ordenación alfabética de tokens (canonical form para comparación)
 *
 * @module AddressNormalizerL2
 * @version 1.0.0
 * @date Febrero 2026
 */

import { normalizeAddressL1, type NormalizeL1Result } from './AddressNormalizerL1';

// ============================================================================
// TIPOS
// ============================================================================

export interface NormalizeL2Result {
  /** Dirección normalizada L2 (solo para matching) */
  normalized: string;
  /** Resultado intermedio L1 */
  l1Result: NormalizeL1Result;
  /** Dirección original */
  original: string;
  /** Transformaciones L2 aplicadas */
  transforms: NormalizeL2Transform[];
  /** Tokens finales tras normalización */
  tokens: string[];
}

export type NormalizeL2Transform =
  | 'ARTICLES_STRIP'
  | 'NUMBERS_STRIP'
  | 'GENERIC_WORDS_STRIP'
  | 'DEDUP_TOKENS'
  | 'ALPHA_SORT';

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Artículos y preposiciones a eliminar.
 * Solo se eliminan como tokens completos, nunca dentro de palabras.
 */
const ARTICLES_AND_PREPS = new Set([
  'DE', 'DEL', 'LA', 'LAS', 'EL', 'LOS',
  'AL', 'A', 'EN', 'POR', 'CON', 'Y',
]);

/**
 * Palabras genéricas que no aportan al matching de nombre de vía.
 * Se eliminan para mejorar coincidencia entre variantes.
 */
const GENERIC_WORDS = new Set([
  'MUNICIPAL', 'PUBLICO', 'PUBLICA', 'NUEVO', 'NUEVA',
  'VIEJO', 'VIEJA', 'ANTIGUO', 'ANTIGUA',
  'SN', 'N',
]);

/**
 * Patrón para detectar tokens puramente numéricos o de kilómetro.
 * Incluye: "5", "12", "KM", "KM5", "5-7"
 */
const NUMERIC_TOKEN_REGEX = /^(?:KM\d*|\d+(?:[-.]\d+)?)$/;

// ============================================================================
// FUNCIONES PRIVADAS
// ============================================================================

/**
 * Divide dirección normalizada L1 en tokens.
 */
function tokenize(text: string): string[] {
  return text.split(/\s+/).filter(t => t.length > 0);
}

/**
 * Elimina artículos y preposiciones.
 * Preserva el primer token si es tipo de vía (CALLE, AVENIDA, etc.)
 */
function stripArticles(tokens: string[]): { result: string[]; modified: boolean } {
  const result = tokens.filter(t => !ARTICLES_AND_PREPS.has(t));
  return { result, modified: result.length !== tokens.length };
}

/**
 * Elimina tokens numéricos (portales, km).
 */
function stripNumbers(tokens: string[]): { result: string[]; modified: boolean } {
  const result = tokens.filter(t => !NUMERIC_TOKEN_REGEX.test(t));
  return { result, modified: result.length !== tokens.length };
}

/**
 * Elimina palabras genéricas redundantes.
 */
function stripGenericWords(tokens: string[]): { result: string[]; modified: boolean } {
  const result = tokens.filter(t => !GENERIC_WORDS.has(t));
  return { result, modified: result.length !== tokens.length };
}

/**
 * Deduplica tokens preservando orden de primera aparición.
 */
function dedup(tokens: string[]): { result: string[]; modified: boolean } {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const t of tokens) {
    if (!seen.has(t)) {
      seen.add(t);
      result.push(t);
    }
  }
  return { result, modified: result.length !== tokens.length };
}

/**
 * Ordena tokens alfabéticamente para forma canónica.
 * Excepción: el tipo de vía (primer token) se mantiene primero.
 */
function alphaSort(tokens: string[]): { result: string[]; modified: boolean } {
  if (tokens.length <= 1) return { result: [...tokens], modified: false };

  const [first, ...rest] = tokens;
  const sorted = [...rest].sort();
  const result = [first, ...sorted];
  const modified = rest.some((t, i) => t !== sorted[i]);
  return { result, modified };
}

// ============================================================================
// API PÚBLICA
// ============================================================================

/**
 * Normalización agresiva L2 de una dirección.
 *
 * ⚠️ SOLO para matching interno, resultado NO apto para mostrar al usuario.
 *
 * @param address Dirección original
 * @returns Resultado con forma canónica para matching
 *
 * @example
 * normalizeAddressL2("C/ de la Iglesia Municipal, 5")
 * // → { normalized: "CALLE IGLESIA", tokens: ["CALLE", "IGLESIA"], ... }
 *
 * @example
 * normalizeAddressL2("Avda. del Nuevo Parque, nº 12")
 * // → { normalized: "AVENIDA PARQUE", tokens: ["AVENIDA", "PARQUE"], ... }
 */
export function normalizeAddressL2(address: string): NormalizeL2Result {
  const l1Result = normalizeAddressL1(address);
  const original = address;
  const transforms: NormalizeL2Transform[] = [];

  if (!l1Result.normalized) {
    return {
      normalized: '',
      l1Result,
      original,
      transforms,
      tokens: [],
    };
  }

  let tokens = tokenize(l1Result.normalized);

  // 1. Strip artículos y preposiciones
  const arts = stripArticles(tokens);
  if (arts.modified) {
    transforms.push('ARTICLES_STRIP');
    tokens = arts.result;
  }

  // 2. Strip tokens numéricos
  const nums = stripNumbers(tokens);
  if (nums.modified) {
    transforms.push('NUMBERS_STRIP');
    tokens = nums.result;
  }

  // 3. Strip palabras genéricas
  const gens = stripGenericWords(tokens);
  if (gens.modified) {
    transforms.push('GENERIC_WORDS_STRIP');
    tokens = gens.result;
  }

  // 4. Deduplicación
  const dd = dedup(tokens);
  if (dd.modified) {
    transforms.push('DEDUP_TOKENS');
    tokens = dd.result;
  }

  // 5. Ordenación alfabética (tipo vía primero, resto alfabético)
  const sorted = alphaSort(tokens);
  if (sorted.modified) {
    transforms.push('ALPHA_SORT');
    tokens = sorted.result;
  }

  return {
    normalized: tokens.join(' '),
    l1Result,
    original,
    transforms,
    tokens,
  };
}

/**
 * Versión simplificada que solo devuelve el string normalizado L2.
 */
export function normalizeL2(address: string): string {
  return normalizeAddressL2(address).normalized;
}

// ============================================================================
// GEO-STR-01 — Strip municipio/provincia en sufijo de dirección postal
// ============================================================================

/**
 * Patrón de tipos de vía reconocidos.
 * Solo se activa el strip cuando la dirección comienza por tipo-de-vía,
 * para proteger topónimos puros (parajes, dehesas, montes…).
 */
const TIPO_VIA_REGEX =
  /^(calle|avenida|plaza|camino|carretera|paseo|ronda|travesia|travesía|callejón|callejon|glorieta|bulevar|via\s|vía\s)/i;

/**
 * Escapa caracteres especiales de regex en una cadena literal.
 */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Elimina el sufijo de municipio/provincia de una dirección postal.
 *
 * Aplica SOLO antes de consultas a geocodificadores externos (CDAU-WFS,
 * CartoCiudad). No forma parte del pipeline L1 (que es conservador).
 *
 * Casos cubiertos:
 *   "Plaza del Padre Poveda, 33 Guadix, Granada"   → "Plaza del Padre Poveda, 33"
 *   "Calle Torreón del Ferro, 13(Guadix)"          → "Calle Torreón del Ferro, 13"
 *   "Calle Almería, 0 (Hernan Valle)"              → "Calle Almería, 0"
 *   "Calle Virgen de Fátima Guadix"                → "Calle Virgen de Fátima"
 *
 * Caso límite protegido:
 *   "Avenida Ciudad de Guadix, 9"                  → sin cambio (municipio en nombre de vía)
 *   "Dehesa de la Alfaguara, Parque Natural"        → sin cambio (sin tipo-de-vía)
 *
 * @param address   Dirección normalizada L1 (puede llevar municipio al final)
 * @param municipio Nombre del municipio a eliminar (comparación case-insensitive)
 * @returns Dirección sin sufijo municipal, o la original si el strip es inseguro
 *
 * @since GEO-STR-01 · 2026-04-08
 */
export function stripMunicipioSuffix(address: string, municipio: string): string {
  if (!address || !municipio) return address;

  // Guardia: solo actuar si tiene tipo de vía (protege topónimos puros)
  if (!TIPO_VIA_REGEX.test(address.trim())) return address;

  // Guardia: solo actuar si tiene número (protege "Avenida Ciudad de Guadix" sin nº)
  if (!/\d/.test(address)) return address;

  const esc = escapeRegex(municipio);

  let result = address
    // 1. Paréntesis con nombre de municipio al final: "...(Guadix)" "...(Hernan Valle)"
    //    Cubre municipios con espacios, tildes o guiones (2-60 chars)
    .replace(/\s*\([^)]{2,60}\)\s*$/, '')
    // 2. ", Municipio" o " Municipio" seguido opcionalmente de ", Provincia" al final.
    //    Solo cuando aparece DESPUÉS de un dígito → evita "Avenida Ciudad de Guadix"
    .replace(
      new RegExp(
        '(\\d[^,]*),?\\s+' + esc + '(?:\\s*,\\s*[A-Za-záéíóúüñÁÉÍÓÚÜÑ ]+)?\\s*$',
        'i'
      ),
      (_match, before) => before.trim()
    )
    .trim()
    // 3. Limpiar coma o punto colgante al final
    .replace(/[,.\s]+$/, '');

  // Guardia: si el resultado queda vacío o muy corto o sin número → devolver original
  if (!result || result.length < 5 || !/\d/.test(result)) return address;

  return result;
}
